import { useMemo, useEffect, useState } from "react";
import { useSearchParams } from "react-router";

// External Libraries
import { Button } from "primereact/button";
import { Dropdown } from "primereact/dropdown";
import { TabView, TabPanel } from "primereact/tabview";

// Local context
import { getSandboxLanguages } from "../contexts/languages";
import { useSandbox, useFilesystem, useSession } from "../contexts/SandboxHooks";
import { useTabs } from "../contexts/Tabs";

// Tab components
import Browser from "./Tabs/SandboxBrowser";
import Editor from "./Tabs/Editor";
import ProxyHistory from "./Tabs/ProxyHistory";
import Tests from "./Tabs/Tests";

// Dialog components
import SandboxStatusLoader from "./Dialogs/SandboxStatusLoader";
import SessionExpiredPage from "./Dialogs/SessionExpiredPage";
import SessionExpiringOverlay from "./Dialogs/SessionExpiringOverlay";
import SubmitFlagDialog from "./Dialogs/SubmitFlagDialog";

// Other local components
import ActionBar from "../../ActionBar/ActionBar";

// Module styles
import S from "../_styles.module.scss";

// Main component
const SandboxControls = ({ tabs, engines, isCtf }) => {
  const { activeTabIndex, setActiveTab } = useTabs();
  const {
    currentLanguage,
    resetSandbox,
    hasEditor,
    hasTests,
    isTestRunning,
    runTests,
    testStatus,
    onLessonFinish,
    isSandboxLessonComplete,
    allStepsCompleted,
    correctFlagSubmitted,
  } = useSandbox();
  const { editor, saveCurrentFile } = useFilesystem();
  const languageOptions = getSandboxLanguages(engines || []);

  // State for flag submission modal
  const [showFlagModal, setShowFlagModal] = useState(false);

  // Build actions & message for action bar
  const Actions = useMemo(() => {
    const actions = [
      {
        label: "Finish lesson",
        icon: "check",
        onClick: () => {
          if (onLessonFinish) {
            onLessonFinish();
          } else {
            console.warn("onLessonFinish callback not provided");
          }
        },
      },
    ];
    return actions;
  }, [testStatus, onLessonFinish]);

  const Message = useMemo(() => {
    if (testStatus?.status === "passed") {
      return "Congratulations, all tests have passed!";
    } else if (correctFlagSubmitted) {
      return "Congratulations, you submitted the correct flag!";
    } else if (allStepsCompleted) {
      return "Congratulations, all steps have been completed!";
    }
  }, [testStatus, correctFlagSubmitted, allStepsCompleted]);

  // Access timeout status. Override with URL param for testing.
  const [searchParams] = useSearchParams();
  const { session } = useSession();
  const timeoutStatus = searchParams.get("timeoutStatus") ?? session.timeout;

  // Browser ctrl/cmd key sequence
  const ctrlKeySeq = /Mac|Mac OS|MacIntel|MacPPC|Mac68K/i.test(navigator.userAgent) ? "⌘" : "Ctrl+";

  // Determine button states
  const saveAndRunDisabled = useMemo(
    () => isTestRunning || (hasEditor && !editor.currentContents),
    [hasEditor, isTestRunning, editor.currentContents]
  );

  // Handle keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = e => {
      // Check for Ctrl+S (Windows/Linux) or Cmd+S (macOS)
      if ((e.ctrlKey || e.metaKey) && e.key === "s") {
        e.preventDefault(); // Prevent browser's default save dialog

        // Only save if editor is dirty
        if (editor.isDirty) {
          saveCurrentFile();
        }
      }

      // Check for Ctrl+R (Windows/Linux) or Cmd+R (macOS)
      if ((e.ctrlKey || e.metaKey) && e.key === "r") {
        e.preventDefault(); // Prevent browser's default reload

        // Handle save and run
        if (!saveAndRunDisabled) {
          handleSaveAndRun();
        }
      }
    };

    // Add event listener
    window.addEventListener("keydown", handleKeyDown);

    // Cleanup
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [editor.isDirty, saveAndRunDisabled, saveCurrentFile, runTests]);

  // Handle save and run tests
  const handleSaveAndRun = async () => {
    // Save file if editor is dirty
    if (hasEditor && editor.isDirty) {
      const saved = await saveCurrentFile();
      if (!saved) {
        console.error("Failed to save file before running tests");
        return;
      }
    }

    // Run tests
    try {
      // Kick off test run without awaiting results
      runTests();
      // Switch over to Tests tab
      const testsTabIndex = tabs.findIndex(tab => tab.type === "tests");
      if (testsTabIndex !== -1) {
        setActiveTab(testsTabIndex);
      }
    } catch (error) {
      console.error("Error running tests:", error);
    }
  };

  // Show session expired page if timed out
  if (timeoutStatus === "expired") {
    return <SessionExpiredPage />;
  }

  return (
    <div className="flex flex-col h-full gap-2 relative">
      {/* Show a status loader while the sandbox is initializing */}
      <SandboxStatusLoader />

      {/* Session Expiring Warning Overlay */}
      <SessionExpiringOverlay />

      <div className={`flex gap-2 ${S.sandboxHeader}`}>
        {isCtf && (
          <Button icon="pi pi-flag" size="small" onClick={() => setShowFlagModal(true)}>
            <span className="ml-1">Submit flag</span>
          </Button>
        )}
        {hasTests && (
          <Button
            icon={hasEditor ? "pi pi-check" : "pi pi-play"}
            disabled={saveAndRunDisabled}
            size="small"
            onClick={handleSaveAndRun}
          >
            <span className="ml-1">
              {hasEditor && editor.isDirty ? "Save code & run tests" : "Run tests"}
            </span>
            <span className="button-text-muted ml-1">{`${ctrlKeySeq}R`}</span>
          </Button>
        )}
        {hasEditor && (
          <Button
            icon="pi pi-save"
            text
            disabled={!editor.isDirty}
            onClick={saveCurrentFile}
            size="small"
          >
            <span className="ml-1">Save code</span>
            <span className="button-text-muted ml-1">{`${ctrlKeySeq}S`}</span>
          </Button>
        )}
        {languageOptions.length > 0 && (
          <Dropdown
            value={currentLanguage}
            options={languageOptions}
            onChange={e => resetSandbox(e.value)}
            pt={{ input: { className: "body-sm" }, item: { className: "body-sm" } }}
          />
        )}

        <Button
          icon="pi pi-refresh"
          outlined
          severity="secondary"
          text
          size="small"
          onClick={() => resetSandbox()}
          className="ml-auto"
          label="Reset sandbox"
        />
      </div>
      <div className={`flex-2 ${S.sandboxContent}`}>
        <TabView
          activeIndex={activeTabIndex}
          onTabChange={e => setActiveTab(e.index)}
          renderActiveOnly={false}
          className="h-full"
          pt={{
            root: { className: "h-full" },
            panelContainer: { className: S.tabContent },
          }}
        >
          {tabs.map(tab => (
            <TabPanel key={tab.title} header={tab.title} contentClassName="h-full">
              {(() => {
                switch (tab.type) {
                  case "app":
                    return <Browser {...tab} />;
                  case "intercept":
                    return <ProxyHistory {...tab} />;
                  case "editor":
                    return <Editor {...tab} />;
                  case "tests":
                    return <Tests {...tab} />;
                  default:
                    return null;
                }
              })()}
            </TabPanel>
          ))}
        </TabView>
      </div>
      {isSandboxLessonComplete && <ActionBar actions={Actions} message={Message} />}

      {/* Flag Submission Modal */}
      <SubmitFlagDialog visible={showFlagModal} onHide={() => setShowFlagModal(false)} />
    </div>
  );
};

export default SandboxControls;
